import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { IOrganizationUnitDataService } from '@nimbus-global/features/shared/services/api/organization-unit/organization-unit-data.service';
import { IOrganizationUnitSelectListItemViewModel } from '@nimbus-global/features/shared/services/api/organization-unit/domain/i-organization-unit-select-list-item.view-model';
import { IWallboardAuthorizedOrganizationUnit } from '../../models/wallboard-data.model';
import { WallboardSelectors } from '../../store/wallboard.selectors';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class WallboardOrganizationUnitDataService extends IOrganizationUnitDataService {
  protected tenantId$: Observable<string>;

  public constructor(
    private readonly store: Store,
    baseHttpService: BaseHttpService,
    authService: IAuthenticationService,
    permissionManagerService: IPermissionManagerService,
  ) {
    super(baseHttpService, authService, permissionManagerService);

    this.tenantId$ = this.store.select(WallboardSelectors.tenantId).pipe(filter((tenantId) => !!tenantId));
    this.store
      .select(WallboardSelectors.bootstrap)
      .pipe(
        filter((bootstrap) => !!bootstrap),
        untilDestroyed(this),
      )
      .subscribe((bootstrap) =>
        this.organizationUnits$.next({
          tenantId: this.getTenantId(),
          organizationUnits: this.toViewModels(bootstrap.authorizedOrganizationUnits),
        }),
      );
  }

  public getTenantId(): string {
    return this.store.selectSnapshot(WallboardSelectors.tenantId);
  }

  private toViewModels(organizationUnits: IWallboardAuthorizedOrganizationUnit[]): IOrganizationUnitSelectListItemViewModel[] {
    const byId: Record<string, IWallboardAuthorizedOrganizationUnit> = Object.fromEntries(organizationUnits.map((x) => [x.id, x]));
    const getLevel = (organizationUnit: IWallboardAuthorizedOrganizationUnit): number =>
      byId[organizationUnit.parentId] ? getLevel(byId[organizationUnit.parentId]) + 1 : 0;

    return organizationUnits.map((organizationUnit) => ({ ...organizationUnit, level: getLevel(organizationUnit) }));
  }
}
